import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Users, FileText, DollarSign, BarChart3, CheckCircle, XCircle, Clock, Eye, Download, Edit, Save, X, Plus, Trash2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

export default function AdminPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [apps, setApps] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [expandedApp, setExpandedApp] = useState(null);
  const [editingPricing, setEditingPricing] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!authLoading && (!user || user.role !== "admin")) navigate("/login"); }, [user, authLoading, navigate]);
  useEffect(() => { if (user?.role === "admin") { fetchStats(); fetchApps(); } }, [user]);
  useEffect(() => { const i = setInterval(() => { if (user?.role === "admin") { fetchStats(); fetchApps(statusFilter); } }, 10000); return () => clearInterval(i); }, [user, statusFilter]);
  useEffect(() => { if (tab === "users") fetchUsers(); if (tab === "pricing") fetchPricing(); }, [tab]);

  const fetchStats = async () => { try { const { data } = await axios.get(`${API}/admin/stats`, getHeaders()); setStats(data); } catch {} };
  const fetchApps = async (status = "") => { try { const { data } = await axios.get(`${API}/admin/applications${status && status !== "all" ? `?status=${status}` : ""}`, getHeaders()); setApps(data.applications || []); } catch {} };
  const fetchUsers = async () => { try { const { data } = await axios.get(`${API}/admin/users`, getHeaders()); setAllUsers(data.users || data || []); } catch {} };
  const fetchPricing = async () => { try { const { data } = await axios.get(`${API}/admin/pricing`, getHeaders()); setPricing(data.pricing || data || []); } catch {} };

  const updateStatus = async (appId, status) => {
    try { await axios.put(`${API}/admin/applications/${appId}/status`, { status, note: `Admin: ${status}` }, getHeaders()); fetchApps(statusFilter); fetchStats(); } catch {}
  };
  const verifyDoc = async (docId, status) => {
    try { await axios.post(`${API}/admin/documents/${docId}/verify`, { status }, getHeaders()); fetchApps(statusFilter); } catch {}
  };
  const savePricing = async (pricingId) => {
    setSaving(true);
    try { await axios.put(`${API}/admin/pricing/${pricingId}`, editValues, getHeaders()); setEditingPricing(null); setEditValues({}); fetchPricing(); } catch {}
    setSaving(false);
  };
  const exportFile = async (type) => {
    try {
      const resp = await axios.get(`${API}/admin/export/${type}`, { ...getHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = url; a.download = `flyinvisa_${type}.csv`; a.click();
    } catch {}
  };

  if (authLoading || !user) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  const tabs = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "applications", label: "Applications", icon: FileText },
    { id: "users", label: "Users", icon: Users },
    { id: "pricing", label: "Pricing", icon: DollarSign },
  ];

  return (
    <div className="min-h-screen px-4 md:px-6 py-8 pt-24 bg-slate-50">
      <div className="max-w-7xl mx-auto">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 text-sm text-slate-400 hover:text-blue-600 mb-6"><ArrowLeft className="w-4 h-4" /> Home</button>
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4">
          <h1 className="heading-font text-3xl font-bold text-slate-900">Admin Dashboard</h1>
          <div className="flex gap-2">
            <button onClick={() => exportFile("applications")} data-testid="export-applications" className="flex items-center gap-1.5 text-xs font-medium text-blue-600 px-4 py-2 rounded-lg border border-blue-200 hover:bg-blue-50"><Download className="w-3.5 h-3.5" /> Export Applications</button>
            <button onClick={() => exportFile("users")} data-testid="export-users" className="flex items-center gap-1.5 text-xs font-medium text-slate-600 px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50"><Download className="w-3.5 h-3.5" /> Export Users</button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${tab === t.id ? "bg-blue-600 text-white" : "bg-white text-slate-500 border border-slate-200 hover:bg-slate-50"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* ===== OVERVIEW ===== */}
        {tab === "overview" && stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Total Users", value: stats.total_users, icon: Users, color: "text-slate-900" },
              { label: "Applications", value: stats.total_applications, icon: FileText, color: "text-blue-600" },
              { label: "Approved", value: stats.approved, icon: CheckCircle, color: "text-emerald-600" },
              { label: "Revenue", value: `$${stats.total_revenue.toFixed(0)}`, icon: DollarSign, color: "text-blue-600" },
            ].map(s => (
              <div key={s.label} className="card-elevated p-5">
                <div className="flex items-center gap-2 mb-2"><s.icon className="w-4 h-4 text-slate-400" /><span className="text-xs text-slate-400">{s.label}</span></div>
                <p className={`heading-font text-2xl font-bold ${s.color}`}>{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* ===== APPLICATIONS ===== */}
        {tab === "applications" && (
          <div>
            <div className="flex items-center gap-3 mb-6">
              <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); fetchApps(v); }}>
                <SelectTrigger className="w-[180px] h-10 bg-white border-slate-200 rounded-lg"><SelectValue placeholder="Filter by status" /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  {["all", "draft", "submitted", "under_review", "processing", "approved", "rejected"].map(s => <SelectItem key={s} value={s}>{s === "all" ? "All Statuses" : s.replace("_", " ").toUpperCase()}</SelectItem>)}
                </SelectContent>
              </Select>
              <span className="text-xs text-slate-400">Auto-refreshes every 10s</span>
            </div>
            <div className="space-y-3">
              {apps.map(a => (
                <div key={a.id} className="card-elevated rounded-xl p-4" data-testid={`admin-app-${a.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="cursor-pointer flex-1" onClick={() => setExpandedApp(expandedApp === a.id ? null : a.id)}>
                      <p className="text-sm font-medium text-slate-900">{a.full_name} — {a.destination_country_name}</p>
                      <p className="text-xs text-slate-400">{a.email} | {a.phone} | {a.visa_type} | {a.processing_type} | {new Date(a.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${a.status === "approved" ? "bg-emerald-50 text-emerald-600" : a.status === "rejected" ? "bg-red-50 text-red-600" : a.status === "submitted" ? "bg-blue-50 text-blue-600" : a.status === "under_review" ? "bg-amber-50 text-amber-600" : "bg-slate-100 text-slate-500"}`}>{a.status.replace("_"," ")}</span>
                      {a.status === "draft" && <button onClick={() => updateStatus(a.id, "submitted")} className="text-xs px-3 py-1 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100">Submit</button>}
                      {a.status === "submitted" && <button onClick={() => updateStatus(a.id, "under_review")} className="text-xs px-3 py-1 rounded-full bg-amber-50 text-amber-600 hover:bg-amber-100"><Eye className="w-3 h-3 inline mr-1" />Review</button>}
                      {(a.status === "under_review" || a.status === "submitted") && <>
                        <button onClick={() => updateStatus(a.id, "approved")} className="text-xs px-3 py-1 rounded-full bg-emerald-50 text-emerald-600 hover:bg-emerald-100"><CheckCircle className="w-3 h-3 inline mr-1" />Approve</button>
                        <button onClick={() => updateStatus(a.id, "rejected")} className="text-xs px-3 py-1 rounded-full bg-red-50 text-red-500 hover:bg-red-100"><XCircle className="w-3 h-3 inline mr-1" />Reject</button>
                      </>}
                      <button onClick={() => setExpandedApp(expandedApp === a.id ? null : a.id)} className="text-xs px-3 py-1 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200">{expandedApp === a.id ? "Close" : "Details"}</button>
                    </div>
                  </div>

                  {expandedApp === a.id && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-xs">
                        {[["Passport", a.passport_number], ["Nationality", a.nationality], ["Travel Date", a.travel_date], ["Price", `$${a.price || "N/A"}`]].map(([l, v]) => (
                          <div key={l}><span className="text-slate-400 block">{l}</span><span className="text-slate-800 font-medium">{v || "N/A"}</span></div>
                        ))}
                      </div>
                      <div className="mb-3">
                        <h4 className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1"><FileText className="w-3 h-3" /> Documents ({a.documents?.length || 0})</h4>
                        {a.documents && a.documents.length > 0 ? (
                          <div className="space-y-2">
                            {a.documents.map(doc => (
                              <div key={doc.id} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg border border-slate-100">
                                <div className="flex items-center gap-2">
                                  <FileText className="w-4 h-4 text-blue-500" />
                                  <div>
                                    <p className="text-xs font-medium text-slate-800">{doc.document_type}</p>
                                    <p className="text-[10px] text-slate-400">{doc.original_name}</p>
                                    {doc.ai_check && <p className={`text-[10px] ${doc.ai_check.is_valid ? "text-emerald-600" : "text-red-500"}`}>AI: {doc.ai_check.is_valid ? "Valid" : "Issues"} {doc.ai_check.issues?.length > 0 && `(${doc.ai_check.issues.join(", ")})`}</p>}
                                  </div>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${doc.status === "verified" ? "bg-emerald-50 text-emerald-600" : doc.status === "rejected" ? "bg-red-50 text-red-500" : "bg-amber-50 text-amber-600"}`}>{doc.status}</span>
                                  {doc.status !== "verified" && <button onClick={() => verifyDoc(doc.id, "verified")} className="text-[10px] text-emerald-600 px-2 py-0.5 rounded border border-emerald-200 hover:bg-emerald-50"><CheckCircle className="w-2.5 h-2.5 inline" /> Verify</button>}
                                  {doc.status !== "rejected" && <button onClick={() => verifyDoc(doc.id, "rejected")} className="text-[10px] text-red-500 px-2 py-0.5 rounded border border-red-200 hover:bg-red-50"><XCircle className="w-2.5 h-2.5 inline" /> Reject</button>}
                                  <a href={`${API.replace('/api', '')}${doc.file_url}`} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-600 px-2 py-0.5 rounded border border-blue-200 hover:bg-blue-50"><Download className="w-2.5 h-2.5 inline" /> View</a>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : <p className="text-xs text-slate-400 bg-slate-50 p-3 rounded-lg">No documents uploaded</p>}
                      </div>
                      {a.status_history?.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-slate-700 mb-2">Timeline</h4>
                          {a.status_history.map((h, j) => (
                            <div key={j} className="flex items-center gap-2 text-[11px] mb-1">
                              <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                              <span className="text-slate-600 font-medium">{h.status}</span>
                              <span className="text-slate-400">— {h.note}</span>
                              <span className="text-slate-300 ml-auto">{new Date(h.date).toLocaleDateString()}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {apps.length === 0 && <p className="text-slate-400 text-center py-8">No applications found</p>}
            </div>
          </div>
        )}

        {/* ===== USERS ===== */}
        {tab === "users" && (
          <div>
            <p className="text-xs text-slate-400 mb-4">{allUsers.length} registered users</p>
            <div className="space-y-2">
              {allUsers.map((u, i) => (
                <div key={i} className="card-elevated rounded-xl p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-sm font-bold">{u.name?.charAt(0)?.toUpperCase() || "?"}</div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{u.name || "No name"}</p>
                      <p className="text-xs text-slate-400">{u.email} {u.phone ? `| ${u.phone}` : ""} {u.nationality ? `| ${u.nationality}` : ""}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${u.role === "admin" ? "bg-blue-50 text-blue-600" : "bg-slate-100 text-slate-500"}`}>{u.role || "user"}</span>
                    <span className="text-[10px] text-slate-300">{u.created_at ? new Date(u.created_at).toLocaleDateString() : ""}</span>
                  </div>
                </div>
              ))}
              {allUsers.length === 0 && <p className="text-slate-400 text-center py-8">No users found</p>}
            </div>
          </div>
        )}

        {/* ===== PRICING ===== */}
        {tab === "pricing" && (
          <div>
            <p className="text-xs text-slate-400 mb-4">{pricing.length} pricing rules — Click Edit to update any rule</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-slate-500 text-xs border-b border-slate-200 bg-slate-50">
                  <th className="text-left py-3 px-3">Destination</th><th className="text-left py-3 px-3">Visa Type</th><th className="text-left py-3 px-3">Speed</th>
                  <th className="text-right py-3 px-3">Price</th><th className="text-right py-3 px-3">Days</th><th className="text-right py-3 px-3">Approval%</th><th className="text-center py-3 px-3">Actions</th>
                </tr></thead>
                <tbody>
                  {pricing.slice(0, 100).map((p, i) => (
                    <tr key={i} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="py-2.5 px-3 text-slate-900 font-medium">{p.destination_country_code}</td>
                      <td className="py-2.5 px-3 text-slate-600">{p.visa_type}</td>
                      <td className="py-2.5 px-3"><span className={`text-xs px-2 py-0.5 rounded-full ${p.processing_type === "urgent" ? "bg-red-50 text-red-500" : p.processing_type === "express" ? "bg-amber-50 text-amber-600" : "bg-slate-100 text-slate-500"}`}>{p.processing_type}</span></td>
                      <td className="py-2.5 px-3 text-right">
                        {editingPricing === p.id ? (
                          <Input value={editValues.price ?? p.price} onChange={e => setEditValues(v => ({ ...v, price: parseFloat(e.target.value) || 0 }))} className="w-20 h-8 text-xs text-right inline-block" />
                        ) : <span className="text-blue-600 font-semibold">${p.price}</span>}
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        {editingPricing === p.id ? (
                          <Input value={editValues.processing_time_days ?? p.processing_time_days} onChange={e => setEditValues(v => ({ ...v, processing_time_days: parseInt(e.target.value) || 0 }))} className="w-16 h-8 text-xs text-right inline-block" />
                        ) : <span className="text-slate-600">{p.processing_time_days}</span>}
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        {editingPricing === p.id ? (
                          <Input value={editValues.approval_rate ?? p.approval_rate} onChange={e => setEditValues(v => ({ ...v, approval_rate: parseFloat(e.target.value) || 0 }))} className="w-16 h-8 text-xs text-right inline-block" />
                        ) : <span className="text-slate-600">{p.approval_rate}%</span>}
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        {editingPricing === p.id ? (
                          <div className="flex gap-1 justify-center">
                            <button onClick={() => savePricing(p.id)} disabled={saving} className="text-[10px] text-white bg-blue-600 px-2.5 py-1 rounded hover:bg-blue-700 disabled:opacity-50"><Save className="w-3 h-3 inline" /> Save</button>
                            <button onClick={() => { setEditingPricing(null); setEditValues({}); }} className="text-[10px] text-slate-500 px-2 py-1 rounded border border-slate-200 hover:bg-slate-50"><X className="w-3 h-3 inline" /></button>
                          </div>
                        ) : (
                          <button onClick={() => { setEditingPricing(p.id); setEditValues({ price: p.price, processing_time_days: p.processing_time_days, approval_rate: p.approval_rate }); }}
                            className="text-[10px] text-blue-600 px-2.5 py-1 rounded border border-blue-200 hover:bg-blue-50"><Edit className="w-3 h-3 inline mr-0.5" /> Edit</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
